// Copyright ©️ 2026 eVolve MEP, LLC

import { useState, useCallback, type ReactNode } from 'react';

import { type PartCatalog, type PartId, useEvolveGet, type PartListPart, type PartDecision } from '@evolvemep/api-ts';
import { Button, Flex, NumberInput } from '@mantine/core';

import { strongFromEntries, strongEntries, strongValues } from 'helpers/strongEntries';
import { useIsMobile } from 'hooks/useIsMobile';
import { CatalogIntrospector, type CatalogIntrospectorProps } from 'modules/Shop/CatalogSetup/CatalogIntrospector';
import { MobileCatalogIntrospector } from 'modules/Shop/CatalogSetup/Mobile/MobileCatalogIntrospector';
import { type PartDecisionsToMake, PartDecisionMakerModal } from 'modules/Shop/CatalogSetup/PartDecisionMakerModal';

export type PartToAdd = {
  part: PartListPart;
  quantity: number;
};

export type PartToAddWithDecisions = PartToAdd & {
  decisions?: PartDecision['decisions'];
};

export type CatalogPartAdderProps = {
  partCatalog: PartCatalog | null;
  /** Consumed by the mobile layout, which renders its own catalog select in place of the modal header's. */
  onCatalogSelected: (catalog: PartCatalog | null) => void;
  /** The list of parts already added, if applicable. Only rendered on desktop, as a right pane. */
  existingPartList?: ReactNode;
} & (
  | {
      withDecisions?: boolean;
      onAddParts: (parts: PartToAdd[]) => Promise<void>;
    }
  | {
      withDecisions: true;
      onAddParts: (parts: PartToAddWithDecisions[]) => Promise<void>;
    }
) &
  Pick<CatalogIntrospectorProps, 'disablePart' | 'disabled'>;

const PartAdder = ({
  part,
  partsToAdd,
  saving,
  onQuantityChange,
  onAdd,
}: {
  part: PartListPart;
  partsToAdd: Record<PartId, PartToAdd>;
  /** PartIds of an in-flight submission */
  saving: PartId[];
  onQuantityChange: (part: PartListPart, quantity: number) => void;
  onAdd: (parts: PartToAdd[]) => void;
}) => {
  // Treat 0 the same as empty
  // oxlint-disable-next-line typescript/prefer-nullish-coalescing
  const quantity = partsToAdd[part.partId]?.quantity || '';
  return (
    <Flex gap="xs" align="center">
      <NumberInput
        value={quantity}
        min={0}
        max={999_999}
        clampBehavior="strict"
        w={80}
        startValue={1}
        allowDecimal={false}
        allowNegative={false}
        onChange={(v) => onQuantityChange(part, Number(v))}
        size="xs"
        disabled={saving.length > 0}
        placeholder="Qty"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            onAdd([{ part, quantity: Number(quantity) }]);
          }
        }}
      />
      <Button
        size="xs"
        onClick={() => onAdd([{ part, quantity: Number(quantity) }])}
        disabled={!quantity || saving.length > 0}
        loading={saving.includes(part.partId)}
      >
        Add
      </Button>
    </Flex>
  );
};

const AddAllButton = ({
  partsToAdd,
  loading,
  onAddAll,
  fullWidth,
}: {
  partsToAdd: Record<PartId, PartToAdd>;
  loading: boolean;
  onAddAll: (parts: PartToAdd[]) => void;
  fullWidth?: boolean;
}) => (
  <Button
    fullWidth={fullWidth}
    onClick={() => onAddAll(strongValues(partsToAdd))}
    disabled={!strongValues(partsToAdd).some((n) => n.quantity > 0)}
    loading={loading}
  >
    Add all ({strongValues(partsToAdd).filter((n) => n.quantity > 0).length})
  </Button>
);

/** Used to select parts (and quantities) from the catalog to add to a parent entity. */
export const CatalogPartAdder = ({
  partCatalog,
  onCatalogSelected,
  withDecisions,
  onAddParts,
  existingPartList,
  disabled,
  disablePart,
}: CatalogPartAdderProps) => {
  const isMobile = useIsMobile();
  const [partsToAdd, setPartsToAdd] = useState<Record<PartId, PartToAdd>>({});
  const onPartQuantityChanged = useCallback((part: PartListPart, quantity: number) => {
    setPartsToAdd((p) => ({
      ...p,
      [part.partId]: {
        part,
        quantity,
      },
    }));
  }, []);

  const [pendingParts, setPendingParts] = useState<PartDecisionsToMake[]>([]);

  const resetPartsToAdd = (submittedParts: PartToAdd[]) =>
    setPartsToAdd((p) =>
      strongFromEntries(strongEntries(p).filter(([partId]) => !submittedParts.some((sp) => sp.part.partId === partId))),
    );

  const { apiCall: getAttributesForParts } = useEvolveGet('shop/partCatalog/part/attributes');

  const [saving, setSaving] = useState<PartId[]>([]);

  const onSubmit = async (submittedParts: PartToAdd[]) => {
    try {
      setSaving(submittedParts.map((p) => p.part.partId));
      if (withDecisions) {
        const { attributes: allPartAttributes } = await getAttributesForParts({
          params: {
            partIds: submittedParts.map((p) => p.part.partId),
          },
        });
        const decisionsToBeMade: PartDecisionsToMake[] = submittedParts.map((p) => ({
          ...p,
          partAttributes: allPartAttributes.find((a) => a.partId === p.part.partId)?.partAttributes ?? [],
        }));
        if (decisionsToBeMade.filter((p) => p.partAttributes.length > 0).length > 0) {
          setPendingParts(decisionsToBeMade);
          return;
        }
      }

      await onAddParts(submittedParts);
      resetPartsToAdd(submittedParts);
    } finally {
      setSaving([]);
    }
  };

  return (
    <>
      {isMobile ? (
        <MobileCatalogIntrospector
          partCatalog={partCatalog}
          onCatalogSelected={onCatalogSelected}
          belowSearchComponent={
            <AddAllButton partsToAdd={partsToAdd} loading={saving.length > 0} onAddAll={onSubmit} fullWidth />
          }
          disablePart={disablePart}
          disabled={disabled}
        >
          {(part) => (
            <PartAdder
              part={part}
              partsToAdd={partsToAdd}
              saving={saving}
              onQuantityChange={onPartQuantityChanged}
              onAdd={onSubmit}
            />
          )}
        </MobileCatalogIntrospector>
      ) : (
        <CatalogIntrospector
          partCatalog={partCatalog}
          rightSideComponent={<AddAllButton partsToAdd={partsToAdd} loading={saving.length > 0} onAddAll={onSubmit} />}
          rightPane={existingPartList}
          disablePart={disablePart}
          disabled={disabled}
        >
          {(part) => (
            <PartAdder
              part={part}
              partsToAdd={partsToAdd}
              saving={saving}
              onQuantityChange={onPartQuantityChanged}
              onAdd={onSubmit}
            />
          )}
        </CatalogIntrospector>
      )}
      {withDecisions && (
        <PartDecisionMakerModal
          parts={pendingParts}
          onClose={() => setPendingParts([])}
          onSubmit={async (submittedParts) => {
            await onAddParts(submittedParts);
            resetPartsToAdd(submittedParts);
          }}
        />
      )}
    </>
  );
};
